import { forwardRef, ChangeEvent } from 'react'
import { NumericInputProps } from './NumericInput.types'
import {
  Container,
  InputWrapper,
  Prefix,
  Suffix,
  Input,
} from './NumericInput.styles'

const formatWithSeparator = (
  value: string | number | undefined,
  separator: string,
  allowDecimals: boolean
): string => {
  if (value === undefined || value === null || value === '') return ''
  const str = typeof value === 'number' ? String(value) : value
  const negative = str.startsWith('-')
  const unsigned = negative ? str.slice(1) : str
  const [intPart, decPart] = allowDecimals ? unsigned.split('.') : [unsigned]
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  const formatted =
    allowDecimals && decPart !== undefined
      ? `${formattedInt}.${decPart}`
      : formattedInt
  return negative ? `-${formatted}` : formatted
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      prefix,
      suffix,
      disabled = false,
      min,
      max,
      step = 1,
      type = 'number',
      allowDecimals = false,
      thousandSeparator,
      className,
      style,
      autoFocus = false,
      readOnly = false,
      id,
      name,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const hasPrefix = !!prefix
    const hasSuffix = !!suffix
    const useSeparator = !!thousandSeparator
    const effectiveType = useSeparator ? 'text' : type

    const displayedValue = useSeparator
      ? formatWithSeparator(value, thousandSeparator, allowDecimals)
      : (value ?? '')

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!useSeparator) {
        onChange?.(event)
        return
      }

      const raw = event.target.value
      const cleaned = raw.split(thousandSeparator).join('')
      const pattern = allowDecimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/
      if (cleaned !== '' && !pattern.test(cleaned)) return

      const synthetic = {
        ...event,
        target: { ...event.target, value: cleaned },
        currentTarget: { ...event.currentTarget, value: cleaned },
      } as ChangeEvent<HTMLInputElement>
      onChange?.(synthetic)
    }

    return (
      <Container className={className} style={style}>
        <InputWrapper $disabled={disabled}>
          {hasPrefix && <Prefix>{prefix as any}</Prefix>}
          <Input
            ref={ref}
            type={effectiveType}
            inputMode={useSeparator ? 'numeric' : undefined}
            value={displayedValue}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            min={useSeparator ? undefined : min}
            max={useSeparator ? undefined : max}
            step={allowDecimals ? step : 1}
            $hasPrefix={hasPrefix}
            $hasSuffix={hasSuffix}
            $disabled={disabled}
            autoFocus={autoFocus}
            readOnly={readOnly}
            id={id}
            name={name}
            aria-label={ariaLabel}
          />
          {hasSuffix && <Suffix>{suffix as any}</Suffix>}
        </InputWrapper>
      </Container>
    )
  }
)

NumericInput.displayName = 'NumericInput'
